import { useEffect, useMemo, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { generatePath, Link } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'

import { useAllBanners } from '@entities/Banners'

import { useModalStore } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods'


import { CategoriesBranch } from '@widgets/categories-branch'

import { CATEGORIES_MODAL_BANNER_CODE, URL_PAGE_CATALOG_1ST_LVL } from '@shared/contants'
import { useCategoriesBranchQuery } from '@shared/generated'
import { cn } from '@shared/lib/cn'

import BannerItem, { shouldRenderBanner } from './banner'

import styles from './styles.module.scss'

type ActiveCategoryShape = {
  id?: string
  slug?: string
  name?: string
}

function ChildrenCategoriesLoading () {
  return (
    <SkeletonTheme>
      {Array.from({
        length: 12,
      }).map((_, index) => {
        return (
          <Skeleton
            width={'100%'}
            height={20}
            style={{
              borderRadius: 6,
            }}
            key={index}
          />
        )
      })}
    </SkeletonTheme>
  )
}

function CategoriesLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={10}
        width={170}
        height={30}
        style={{
          borderRadius: 6,
          marginBottom: 16,
        }}
      />
    </SkeletonTheme>
  )
}

export interface ModalCategoriesProps {
  initialCategorySlug?: string
}

export default function ModalCategories ({ initialCategorySlug }: ModalCategoriesProps) {
  const isTablet = useMediaQuery('(max-width: 1179px)')

  const { closeModal } = useModalStore()
  const { shopId } = useAddressStore()
  const { data: categoriesBranch, loading: loadingCategoriesBranch } =
    useCategoriesBranchQuery({
      variables: {
        shopId,
      },
    })

  const { banners } = useAllBanners({
    bannerType: CATEGORIES_MODAL_BANNER_CODE,
    style: '400xauto',
    limit: 1,
  })

  const hasBanner = !isTablet && banners && banners.length > 0 && shouldRenderBanner(banners[0])

  const [ activeCategory, setActiveCategory ] = useState<ActiveCategoryShape>({
    slug: initialCategorySlug
  })

  useEffect(() => {
    const categories = categoriesBranch?.categoriesBranch

    setActiveCategory((activeCategory) => {
      if (!activeCategory?.id && categories) {
        const category = categories.find((c) => c?.slug === initialCategorySlug) ?? categories[0]

        return {
          id: category?.id as string,
          name: category?.name as string,
          slug: category?.slug as string
        }
      }

      return activeCategory
    })

  }, [ categoriesBranch, initialCategorySlug ])

  const CHILDREN_CATEGORIES = useMemo(() => {
    return (
      categoriesBranch?.categoriesBranch?.find((c) => c?.id == activeCategory?.id)
        ?.childrens || []
    )
  }, [ activeCategory, categoriesBranch?.categoriesBranch ])

  const handleChooseCategory = (item: { id?: string | null; name?: string | null; slug?: string | null }) => {
    if (!item?.id) return
    setActiveCategory({
      id: item.id,
      name: item.name || undefined,
      slug: item.slug || undefined
    })
  }

  if (isTablet) {
    return <CategoriesBranch />
  }

  return (
    <div className={cn(styles.root, { [styles._noBanner]: !hasBanner })}>
      <div className={styles.root__left}>
        {loadingCategoriesBranch && <CategoriesLoading />}
        {!loadingCategoriesBranch &&
          categoriesBranch?.categoriesBranch?.map((item) => {
            if (!item) return null
            return (
              <Link
                key={item?.id}
                className={cn(styles.root__link, {
                  [styles._active]: item?.id == activeCategory?.id,
                })}
                to={generatePath(URL_PAGE_CATALOG_1ST_LVL, { categorySlug: item?.slug as string })}
                onClick={() => closeModal('categories')}
                onMouseEnter={() => handleChooseCategory(item)}
              >
                <img
                  className={styles.root__icon}
                  src={item?.icon?.url as string}
                  alt=""
                  loading="lazy"
                  fetchPriority="low"
                />
                {item?.name}
              </Link>
            )
          })}
      </div>

      {activeCategory?.id && (
        <div className={styles.root__right}>
          <div className={styles.root__subcategories}>
            {activeCategory && (
              <h5 className={styles.root__subcategoriesTitle}>
                {activeCategory.name}
              </h5>
            )}
            <div className={styles.root__subcategoriesList}>
              {loadingCategoriesBranch && <ChildrenCategoriesLoading />}

              {!loadingCategoriesBranch &&
                CHILDREN_CATEGORIES.map((item, index) => {
                  return (
                    <Link
                      className={styles.root__children}
                      to={generatePath(URL_PAGE_CATALOG_1ST_LVL, { categorySlug: item?.slug as string })}
                      onClick={() => closeModal('categories')}
                      key={`${item?.id ?? item?.slug ?? index}`}
                    >
                      {item?.name}
                    </Link>
                  )
                })}
            </div>
          </div>

          {hasBanner && (
            <div className={styles.root__banner}>
              <BannerItem
                title={banners[0]?.name as string}
                description={banners[0]?.button_text as string}
                image={banners[0]?.bannerSlide?.url as string}
                imageMobile={banners[0]?.bannerMobileSlide?.url as string}
                link={banners[0]?.url as string}
                openInNewTab={banners[0]?.optionOpenInNewWindow || false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
